import { useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'

interface CanvasFireworksProps {
    enabled?: boolean
}

export const Fireworks = ({ enabled = true }: CanvasFireworksProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [params, setParams] = useState(() => computeParams())

    useEffect(() => {
        if (!enabled) return

        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return
        const safeCtx = ctx

        let W = window.innerWidth
        let H = window.innerHeight

        const targetRatio = 685 / 552
        const currentRatio = W / H

        if (currentRatio > targetRatio) {
            W = H * targetRatio
        } else {
            H = W / targetRatio
        }

        canvas.width = W
        canvas.height = H

        const handleResize = () => {
            let newW = window.innerWidth
            let newH = window.innerHeight
            const ratio = newW / newH

            if (ratio > targetRatio) {
                newW = newH * targetRatio
            } else {
                newH = newW / targetRatio
            }

            canvas.width = newW
            canvas.height = newH
            W = newW
            H = newH
            setParams(computeParams())
        }

        window.addEventListener('resize', handleResize)

        class Particle {
            x: number
            y: number
            vx: number
            vy: number
            alpha: number
            hue: number

            constructor(x: number, y: number, hue: number) {
                this.x = x
                this.y = y
                const angle = Math.random() * Math.PI * 2
                const speed = (Math.random() * 5 + 2) * params.speedScale
                this.vx = Math.cos(angle) * speed
                this.vy = Math.sin(angle) * speed
                this.alpha = 1
                this.hue = hue
            }

            update(): boolean {
                this.vx *= 0.98
                this.vy *= 0.98
                this.vy += 0.05
                this.x += this.vx
                this.y += this.vy
                this.alpha -= 0.015
                return this.alpha <= 0
            }

            draw(): void {
                safeCtx.beginPath()
                safeCtx.arc(
                    this.x,
                    this.y,
                    params.particleRadius,
                    0,
                    Math.PI * 2
                )
                safeCtx.fillStyle = `hsla(${this.hue}, 100%, 50%, ${this.alpha})`
                safeCtx.fill()
            }
        }

        class Firework {
            x: number
            y: number
            vx: number
            vy: number
            ay: number
            hue: number
            targetY: number

            constructor() {
                this.x = Math.random() * W
                this.y = H
                this.vx = Math.random() * params.vxRange * 2 - params.vxRange
                this.vy = -2 * (Math.random() * params.vyRange + params.vyMin)
                this.ay = 0.05
                this.hue = Math.random() * 360
                this.targetY = Math.random() * (H * 0.9 - H * 0.2) + H * 0.1
            }

            update(): boolean {
                this.vy += this.ay
                this.x += this.vx
                this.y += this.vy
                if (this.y <= this.targetY) {
                    this.explode()
                    return true
                }
                return false
            }

            draw(): void {
                safeCtx.beginPath()
                safeCtx.arc(
                    this.x,
                    this.y,
                    params.particleRadius,
                    0,
                    Math.PI * 2
                )
                safeCtx.fillStyle = `hsl(${this.hue}, 100%, 50%)`
                safeCtx.fill()
            }

            explode(): void {
                for (let i = 0; i < 100; i++) {
                    particles.push(new Particle(this.x, this.y, this.hue))
                }
            }
        }

        const particles: Particle[] = []
        const fireworks: Firework[] = []
        let animationFrameId: number

        const animate = () => {
            ctx.clearRect(0, 0, W, H)

            if (fireworks.length === 0 || Math.random() < 0.05) {
                fireworks.push(new Firework())
            }

            for (let i = fireworks.length - 1; i >= 0; i--) {
                fireworks[i].draw()
                if (fireworks[i].update()) {
                    fireworks.splice(i, 1)
                }
            }

            for (let i = particles.length - 1; i >= 0; i--) {
                particles[i].draw()
                if (particles[i].update()) {
                    particles.splice(i, 1)
                }
            }

            animationFrameId = requestAnimationFrame(animate)
        }

        animate()

        return () => {
            cancelAnimationFrame(animationFrameId)
            window.removeEventListener('resize', handleResize)
        }
    }, [enabled, params])

    return <canvas ref={canvasRef} className={styles.canvas} />
}

function computeParams() {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const minSide = Math.min(vw, vh)

    return {
        particleRadius: Math.max(1.5, minSide / 200),
        vxRange: minSide / 120,
        vyMin: minSide / 140,
        vyRange: minSide / 120,
        speedScale: minSide / 600,
    }
}
